import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { when } from "@/lib/trust";

function RequestRow({ r, onDone }) {
  const [note, setNote] = useState("");
  const decide = async (decision) => {
    try { await api.post(`/staff/username-requests/${r.request_id}/decide`, { decision, note }); toast.success(decision === "approve" ? `Now @${r.requested_username}` : "Request rejected"); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="panel flex flex-wrap items-center gap-3 p-4" data-testid={`username-req-${r.request_id}`}>
      <div className="min-w-[12rem] flex-1">
        <p className="font-mono text-sm text-slate-200">@{r.current_username} <span className="text-slate-500">→</span> <span className="text-amber-200">@{r.requested_username}</span></p>
        <p className="text-xs text-slate-500">{when(r.created_at)}{r.reason && ` · ${r.reason}`}</p>
        {r.status !== "pending" && <p className="text-xs text-slate-400">{r.status}{r.decided_by_username && ` by @${r.decided_by_username}`}{r.note && ` · ${r.note}`}</p>}
      </div>
      {r.status === "pending" && <>
        <input className="field w-full text-xs sm:w-56" placeholder="Note to the member (optional)" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} data-testid={`username-req-note-${r.request_id}`} />
        <button className="btn-gold text-xs" onClick={() => decide("approve")} data-testid={`username-req-approve-${r.request_id}`}><Check className="h-3.5 w-3.5" />Approve</button>
        <button className="btn-outline text-xs" onClick={() => decide("reject")} data-testid={`username-req-reject-${r.request_id}`}><X className="h-3.5 w-3.5" />Reject</button>
      </>}
    </div>
  );
}

export function UsernameRequests() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const { data = [] } = useQuery({ queryKey: ["staff", "username-requests", status], queryFn: () => api.get("/staff/username-requests", { params: { status } }).then((r) => r.data.items) });
  return (
    <div className="space-y-3" data-testid="staff-username-requests">
      <select className="field w-auto text-xs" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status" data-testid="username-req-status-filter">
        {["pending", "approved", "rejected", "cancelled", "all"].map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
      </select>
      {data.map((r) => <RequestRow key={r.request_id} r={r} onDone={() => qc.invalidateQueries({ queryKey: ["staff", "username-requests"] })} />)}
      {!data.length && <p className="text-sm text-slate-500" data-testid="username-req-empty">No {status === "all" ? "" : status} requests.</p>}
    </div>
  );
}
